<template>
  <div ref="wrapper">
    <div
      :id="`${group.id}group`"
      :class="
        cx(groupNameStyles, {
          lastItem: isLastItem,
        })
      "
      @click="clickHandler"
      class="point-group-name"
    >
      <PointGroupName
        :group="group"
        :has-edit-permission="hasEditPermission"
        :can-access-to-point-list="canAccessToPointList"
        @bulkAddLocation="openBulkAddLocationModal"
        :has-expanding-list="hasExpandingList"
        :targetLocationCount="getTargetLocationCount"
      />
    </div>
    <template v-if="loadingPointList && hasExpandingList">
      <v-btn loading flat small color="primary" class="loading" />
    </template>
    <TransitionExpand>
      <PointList
        v-if="!loadingPointList && isTargetGroup && hasExpandingList"
        :point-list="pointListForUI"
        :list-open="isTargetGroup"
        :has-edit-permission="hasEditPermission"
      />
    </TransitionExpand>
    <LocationModalBulkAdd
      v-if="showLocationModal"
      v-model="showLocationModal"
      :point-group="group"
      :geocoder="geocoder"
      @save="setTargetGroup"
    />
  </div>
</template>

<script>
import { actions, getters } from '@/vuex/modules/pointGroupOperation/constants'
import pointGroupStyles from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/PointGroup/pointGroupStyles'
import TransitionExpand from '@/components/Shared/Transition/TransitionExpand'
import PointList from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/Point/PointList'
import PointGroupName from './PointGroupName'
import LocationModalBulkAdd from './LocationModalBulkAdd'
import RouteHelper from '@/mixins/RouteHelper'
import { cx } from 'emotion'
import { locationGroupType } from '../constants'

export default {
  name: 'PointGroup',
  components: {
    PointList,
    TransitionExpand,
    PointGroupName,
    LocationModalBulkAdd,
  },
  mixins: [RouteHelper],
  props: {
    group: {
      type: Object,
      default: null,
    },
    index: {
      type: Number,
      default: null,
    },
    length: {
      type: Number,
      default: null,
    },
    isTargetGroup: Boolean,
    locationGroupType: {
      type: String,
      default: locationGroupType.CUSTOM_LOCATION,
    },
    hasEditPermission: {
      type: Boolean,
      default: false,
    },
    geocoder: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      showLocationModal: false,
      hasExpandingList:
        this.locationGroupType === locationGroupType.CUSTOM_LOCATION,
    }
  },
  computed: {
    cx() {
      return cx
    },
    isLastItem() {
      return this.index === this.length - 1
    },
    groupNameStyles() {
      return pointGroupStyles.isSelected(this.isTargetGroup)
    },
    targetGroup() {
      return this.$store.getters[getters.targetGroup]
    },
    pointListForUI() {
      return this.$store.getters[getters.pointListForUI]
    },
    showTooltip() {
      return !this.isTargetGroup
    },
    hasUpdates() {
      return this.$store.getters[getters.targetGroupHasUpdates]
    },
    loadingPointList() {
      return this.$store.getters[getters.loadingPointList] && this.isTargetGroup
    },
    isSystemOwner() {
      return this.$store.getters['account/isSystemOwner']
    },
    isSuperAdmin() {
      return this.$store.getters['account/isSuperAdmin']
    },
    getTargetLocationCount() {
      const locationCount = this.pointListForUI.filter(group => !group.toDelete)
        .length
      const activeLocationCount = this.pointListForUI.filter(
        group => !group.toDelete && group.status === 'active',
      ).length
      return { locationCount, activeLocationCount }
    },
    canAccessToPointList() {
      switch (this.group.accessibleType) {
        case 'public':
          return true
        case 'private':
          return this.isSystemOwner
        case 'completelyPrivate':
          return this.isSuperAdmin
        default:
          return false
      }
    },
    isEditingPoiCategory() {
      return this.$store.getters[getters.isEditingPoiCategory]
    },
  },
  watch: {
    isTargetGroup(newVal) {
      if (newVal) this.scrollIntoView()
    },
  },
  methods: {
    async clickHandler() {
      if (!this.canAccessToPointList) {
        await this.$confirm(undefined, {
          okText: '閉じる',
          onlyOk: true,
          body: 'この地点グループに属する地点の閲覧権限がありません。',
        })
        return
      }
      if (this.isTargetGroup) {
        if (this.hasUpdates || this.isEditingPoiCategory) {
          // 編集中なら何も起こらない
        } else {
          // 更新がなければアコーディオンを閉じる
          this.$store.dispatch(actions.CLEAR_TARGET_GROUP)
        }
      } else {
        // 別の地点グループに切り替える場合、変更があれば確認する
        if (this.hasUpdates) {
          const result = await this.$confirm(
            '追加・編集が破棄されますがよろしいですか？',
          )
          if (!result) return
        } else if (this.isEditingPoiCategory) {
          const result = await this.$confirm(
            'カテゴリーグループの地点変更中ですがよろしいですか？',
          )
          if (!result) return
        }
        if (this.locationGroupType === 'poi_location') {
          this.setTargetGroupNotPoint(this.targetGroup)
          return
        }
        this.setTargetGroup()
      }
    },
    setTargetGroup() {
      this.$store.dispatch(actions.SET_TARGET_GROUP, {
        groupData: this.group,
      })
    },
    setTargetGroupNotPoint() {
      this.$store.commit(actions.SET_TARGET_GROUP_NOT_POINT, {
        groupData: this.group,
      })
    },
    scrollIntoView() {
      setTimeout(() => {
        this.$refs.wrapper.scrollIntoView({ behavior: 'smooth' })
      }, 500)
    },
    openBulkAddLocationModal() {
      this.showLocationModal = true
    },
  },
}
</script>

<style scoped lang="scss">
.point-group-name {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 1;
}
.editButton {
  cursor: context-menu;
  &:hover {
    transform: scale(1.2);
    color: #3b00ff;
  }
}

.lastItem {
  border: none;
}

.loading {
  width: 100%;
  height: 32px;
}
</style>
